import { useState, useRef, useEffect } from 'react';
import icon from "../../images/logo.svg";
import Chip from './Chip';
import apiClient from '../../business/network/ApiClient';
import { useMilaContext } from '../context/useMilaContext';
import utils from "../../business/helper/Utils";

const ALLOWED_FILE_TYPES = ['txt', 'pdf', 'doc', 'docx', 'rtf', 'csv', 'md', 'otd', 'xls', 'xlsx'];
const MAX_FILES_ALLOWED = 3;

export default function MilaUploader({ threadId, onError, onFilesReady, onUploadStatusFinished, disableButton = false, onResetUploader, currentQuestionIndex }) {
    const [files, setFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState({});
    const fileInputRef = useRef(null);
    const milaContext = useMilaContext();

    const resetUploader = () => {
        setFiles([]);
        setUploadingFiles({});
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    const handleFileSelection = () => {
        if (files.length >= MAX_FILES_ALLOWED) {
            onError(`Maximum ${MAX_FILES_ALLOWED} files allowed per message.`);
            return;
        }

        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];

        if (selectedFile && selectedFile.size === 0) {
            onError(`This file is empty. Please upload a file with content.`);
            resetUploader();
            return;
        }

        if (!selectedFile) return;

        fileInputRef.current.value = '';

        const extension = selectedFile.name.split('.').pop().toLowerCase();

        if (!ALLOWED_FILE_TYPES.includes(extension)) {
            onError(`This file type is not supported. Please upload ${ALLOWED_FILE_TYPES.join(', ')}.`);
            resetUploader();
            return;
        }

        const fileId = Date.now().toString();

        setFiles(prevFiles => [...prevFiles, {
            id: fileId,
            file: selectedFile,
            name: selectedFile.name,
            extension
        }]);

        // Initialize retry count only once per file
        if (retryCount[fileId] === undefined) {
            setRetryCount(prev => ({ ...prev, [fileId]: 0 }));
        }
    };

    const handleDelete = async (fileId) => {
        const fileObject = uploadingFiles[fileId];

        const cleanupFileState = () => {
            setFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));

            if (milaContext?.uploadedFiles?.length > 0) {
                milaContext.uploadedFiles = milaContext.uploadedFiles.filter(
                    file => file.file_id !== fileObject?.fileResponse?.file_id
                );
            }

            setUploadingFiles(prev => {
                const newState = { ...prev };
                delete newState[fileId];
                return newState;
            });
        };

        // No tracked upload for this chip (e.g. a duplicate file that was never
        // re-uploaded) — just remove it locally so the user is never stuck.
        if (!fileObject) {
            cleanupFileState();
            return;
        }

        if (fileObject.status === 'error') {
            cleanupFileState();
        } else {
            // For all other statuses (pending, running, or completed), attempt deletion
            const response = await apiClient.deleteUploadedFile(
                fileObject.fileResponse?.file_id || fileId,
                threadId,
                "thread"
            );

            if (response.status !== "error") {
                cleanupFileState();
            }
        }
    };

    const uploadFiles = async () => {
        if (files.length === 0) return null;

        const uploadPromises = files.map(async (fileItem) => {
            // Already handled by this uploader in the current session (in-flight
            // or completed). uploadFiles re-runs over the whole list on every
            // change, so skip anything we're already tracking — never re-upload
            // it and never mistake a legitimately-uploaded file for a duplicate.
            if (uploadingFiles[fileItem.id]) {
                return null;
            }

            // A brand-new chip whose name matches a file already uploaded in this
            // thread is a real duplicate the user just added. Don't leave a stuck
            // chip with no upload state — tell the user and drop it so it stays
            // removable.
            const isDuplicate = milaContext?.uploadedFiles.findIndex(
                f => f.file_name === fileItem.file?.name && f.thread_id === threadId
            ) !== -1;

            if (isDuplicate) {
                onError(`This file has already been uploaded.`);
                setFiles(prevFiles => prevFiles.filter(file => file.id !== fileItem.id));
                return null;
            }

            try {
                setUploadingFiles(prev => ({
                    ...prev,
                    [fileItem.id]: { status: 'pending' }
                }));

                const data = await apiClient.uploadFile(fileItem.file, threadId);

                // Set the background job ID for status checking
                setUploadingFiles(prev => ({
                    ...prev,
                    [fileItem.id]: {
                        status: 'running',
                        fileResponse: data?.fileResponse,
                        name: fileItem.name,
                        extension: fileItem.extension,
                        timeout: data?.fileUploadTimeoutMinutes
                    }
                }));

                return {
                    fileId: fileItem.id,
                    fileResponse: data?.fileResponse,
                };
            } catch (error) {

                if (error?.status === 422) {
                    setFiles(prevFiles => prevFiles.filter(file => file.id !== fileItem.id));
                    onError(`${error.response && error.response.data ? error.response.data : error.message}`);
                    setUploadingFiles(prev => ({
                        ...prev,
                        [fileItem.id]: { status: 'deleted' }
                    }));
                    return null;
                }

                setUploadingFiles(prev => ({
                    ...prev,
                    [fileItem.id]: { status: 'error' }
                }));
                onError(`${error.response && error.response.data ? error.response.data : error.message}`);
                return null;
            }
        });

        return Promise.all(uploadPromises);
    };

    useEffect(() => {
        if (onFilesReady) {
            onFilesReady(uploadFiles);
        }
    }, [files]);

    const [isFileUploadActive, setIsFileUploadActive] = useState(false);

    useEffect(() => {
        const process = async () => {
            var active = await apiClient.isFileUploadActive();
            setIsFileUploadActive(active);
        }
        process();
        onResetUploader(resetUploader);
    }, []);

    const [retryCount, setRetryCount] = useState({});

    useEffect(() => {
        const statusCheckInterval = {};

        Object.entries(uploadingFiles).forEach(([fileId, fileStatus]) => {
            if ((fileStatus.status === 'pending' || fileStatus.status === 'running')
                && fileStatus.fileResponse?.background_job_id && fileStatus.fileResponse?.file_id) {

                const timeoutMinutes = fileStatus.timeout || 10;
                const checkIntervalSeconds = 3;
                const maxRetries = (timeoutMinutes * 60) / checkIntervalSeconds;

                statusCheckInterval[fileId] = setInterval(async () => {
                    retryCount[fileId]++;

                    // Check if max retries exceeded
                    if (retryCount[fileId] > maxRetries) {
                        console.error(`File ${fileId} exceeded max retries (${maxRetries}) within ${timeoutMinutes} minutes`);

                        setUploadingFiles(prev => ({
                            ...prev,
                            [fileId]: {
                                ...prev[fileId],
                                status: 'error',
                                errorMessage: `File upload check timed out after ${timeoutMinutes} minutes. Please try uploading again.`
                            }
                        }));

                        onError(`Oops! File too complex to process. Try reducing or splitting the file.`);

                        setRetryCount(prev => {
                            const updated = { ...prev };
                            delete updated[fileId];
                            return updated;
                        });
                        clearInterval(statusCheckInterval[fileId]);
                        delete statusCheckInterval[fileId];
                        return;
                    }

                    try {
                        const response = await apiClient.checkFileUploadStatus(fileStatus.fileResponse);
                        if (!response || !response.status || response.status !== 200) {
                            throw new Error(`Status check failed: ${response.status}`);
                        }

                        if (response.data) {
                            const { status } = response.data;

                            if (status === 'error') {
                                onError(`Oops! This file couldn’t be processed. Please try again.`);
                            }

                            setUploadingFiles(prev => ({
                                ...prev,
                                [fileId]: {
                                    ...prev[fileId],
                                    status
                                }
                            }));

                            if (status === 'stored') {
                                if (milaContext?.uploadedFiles.findIndex(f => f.file_id === fileStatus.fileResponse.file_id
                                    && f.thread_id === threadId) === -1) {
                                    milaContext?.uploadedFiles.push({
                                        file_id: fileStatus.fileResponse.file_id,
                                        thread_id: threadId,
                                        file_name: fileStatus.name,
                                        file_extension: fileStatus.extension,
                                        question_index: currentQuestionIndex
                                    });
                                }
                            }

                            if (['stored', 'error', 'deleted'].includes(status)) {
                                clearInterval(statusCheckInterval[fileId]);
                                delete statusCheckInterval[fileId];
                                delete retryCount[fileId];
                            }
                        }
                        else {
                            throw new Error('No data returned from status check');
                        }
                    } catch (error) {
                        console.error('Error checking upload status:', error);
                        setUploadingFiles(prev => ({
                            ...prev,
                            [fileId]: {
                                ...prev[fileId],
                                status: 'error',
                                errorMessage: error.message
                            }
                        }));

                        clearInterval(statusCheckInterval[fileId]);
                        delete statusCheckInterval[fileId];
                        delete retryCount[fileId];
                    }
                }, 3000);
            }
        });

        return () => {
            Object.values(statusCheckInterval).forEach(interval => clearInterval(interval));
        };
    }, [uploadingFiles]);

    const isUploading = Object.values(uploadingFiles).some(
        file => ['pending', 'running'].includes(file.status)
    );

    useEffect(() => {
        onUploadStatusFinished(!isUploading, uploadingFiles);
    }, [isUploading]);

    return isFileUploadActive ?

        <div className="flex items-center gap-2 flex-wrap px-3">
            {(files.length < MAX_FILES_ALLOWED || isUploading === true) && (
                <>
                    <button
                        type="button"
                        className={`flex items-center justify-center h-8 w-8 rounded-full bg-[#F1EFFF] text-[#303C54] ${disableButton ? 'cursor-not-allowed opacity-50' : ''}`}
                        onClick={handleFileSelection}
                        disabled={isUploading || disableButton}
                    >
                        {isUploading ? (
                            <img
                                src={icon}
                                title="Please wait while your file is being uploaded"
                                className="animate-spin-infinite h-5 w-5"
                            />
                        ) : (
                            <span className="text-2xl leading-none pb-[2px] pt-[1px]" title="Upload your file and ask Mila about it">+</span>
                        )}
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                        accept={ALLOWED_FILE_TYPES.map(ext => `.${ext}`).join(',')}
                    />
                </>
            )}
            {files.map(file => {
                const uploadStatus = uploadingFiles[file.id]?.status;

                return (
                    <>
                        <Chip
                            key={file.id}
                            label={file.name?.length > 30 ? file.name.substring(0, 29) + '...' : file.name}
                            title={file.name}
                            variant="outlined"
                            icon={utils.getFileIcon(file.extension)}
                            onDelete={() => handleDelete(file.id)}
                            disableButton={disableButton}
                            className={uploadStatus === 'error' ? 'border-red-500' : uploadStatus === 'stored' ? 'border-green-500' : ''}
                        />
                    </>
                );
            })}
        </div> : <div className="flex items-center gap-2 flex-wrap"></div>
}